<template>
  <NavBar
    :class="['navbar-menu', { 'navbar-menu--transparent': isTransparent }]"
    :transparent="isTransparent"
  >
    <template #left>
      <slot name="left" />
    </template>
    <template #center>
      <nav class="navbar-menu__tabs">
        <Tab
          v-for="(item, index) in menu"
          :key="index"
          class="navbar-menu__tab"
          :actived="isMenuItemActive(item)"
          :disabled="isMenuItemDisabled(item)"
          data-testid="navbar-item"
          @set-tab="changeTab(item)"
        >
          <BaseIcon
            v-if="item.icons"
            class="navbar-menu__icon"
            :colors="getIcon(item).color || 'currentColor'"
            width="24px"
            height="24px"
            :icon="getIcon(item).name"
            aria-hidden="true"
          />
          {{ item.label }}
        </Tab>
      </nav>
    </template>
    <template #right>
      <slot name="right" />
    </template>
  </NavBar>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator';

import { BaseIcon, NavBar, Tab } from '@warrenbrasil/nebraska-web';
import { MenuItem } from './types';
import { namespace } from 'vuex-class';
import { AccountStates } from '@/modules/common/services/http/account';

const CustomerModule = namespace('CustomerModule');

@Component({
  components: {
    BaseIcon,
    NavBar,
    Tab
  }
})
export default class NavbarMenu extends Vue {
  @Prop({ type: Boolean, default: false })
  readonly isTransparent?: boolean;

  @Prop({ type: Array, required: true })
  readonly menu!: MenuItem[];

  @CustomerModule.Getter('getAccountStatus')
  readonly accountStatus!: AccountStates;

  @CustomerModule.Getter('isCustomerPJ')
  readonly isCustomerPJ!: boolean;

  isMenuItemActive(item: MenuItem) {
    return this.$route.matched.some(matched => matched.name === item.to);
  }

  isMenuItemDisabled(item: MenuItem) {
    if (item.to === 'home') {
      return false;
    } else {
      return (
        !this.accountStatus?.clientEnabled ||
        !this.accountStatus?.hasInvestmentProfile
      );
    }
  }

  getMenuItemHref(item: MenuItem) {
    return `#/${item.to}`;
  }

  getIcon(item: MenuItem) {
    return this.isMenuItemActive(item)
      ? item.icons.active
      : item.icons.inactive;
  }

  changeTab(item: MenuItem) {
    if (
      item.to === 'home' ||
      this.isCustomerPJ ||
      (this.accountStatus?.hasInvestmentProfile &&
        this.accountStatus.clientEnabled)
    ) {
      this.$emit('tab-select', item.label);
      if (this.$route.name !== item.to) {
        this.$router.push({ name: item.to });
      }
    }
  }
}
</script>

<style lang="less" scoped>
@mobile-height-navbar: 56px;

.navbar-menu {
  position: fixed;
  width: 100%;
  top: 0;
  z-index: 15;

  &__icon {
    display: none;
  }

  &__tabs {
    height: 100%;
    margin: auto;
    max-width: 1200px;
    padding: 0px @size-spacing-x600;
    display: flex;
  }

  &__tab {
    text-transform: capitalize;
  }

  &--transparent & {
    &__tabs {
      background: transparent;
    }

    &__tab {
      color: @text-over-image;
      &:hover,
      &.tab-container--actived {
        border-color: @divider-navbar !important;
      }
      &.tab-container--disabled {
        color: @text-disabled;
        opacity: 0.7;
      }
    }
  }
}

// prettier-ignore
.to-sm({
  .navbar-menu {
    height: @mobile-height-navbar;

    &--transparent {
      border: none;
    }

    &__icon {
      display: block;
    }

    &__tabs {
      position: fixed;
      bottom: 0;
      left: 0;
      width: 100%;
      background: @element-negative !important;
      z-index: 10;
      padding: 0px @size-spacing-x300;
      height: @mobile-height-navbar;
      border-top: 1px solid @divider-primary !important;
    }

    &__tab {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      color: @text-secondary !important;
      border: none;

      &.tab-container--actived,
      &:hover {
        color: @text-primary !important;
      }

      &:not(&.tab-container--actived) {
        font-weight: normal;
      }
    }
  }
});

/deep/ .navbar-container__center {
  width: 100%;
  height: 100%;
}
</style>
